"use client";

import { EventStatus } from "@finbackoffice/enums";
import { getTimefilterPayloadForSocket, sortByScheduledTime } from "@finbackoffice/fe-core";
import { useWebsocketUpdates } from "@finbackoffice/site-core";
import {
    HoursRange,
    IClientError,
    IEventFeedResponse,
    ISubscriptionPayloadParams,
} from "@finbackoffice/websocket-client";
import { useCallback, useEffect, useMemo, useState } from "react";

const statuses = [EventStatus.NotActive, EventStatus.Live, EventStatus.Settled, EventStatus.Ended];

type IGamesState = {
    loading: boolean;
    data: IEventFeedResponse[];
};

export const usePrematchTournament = (
    tournamentId: number,
    hoursRange: HoursRange,
    initialEvents?: IEventFeedResponse[],
) => {
    const [games, setGames] = useState<IGamesState>(() => ({
        loading: !initialEvents?.length,
        data: initialEvents ?? [],
    }));
    const [gamesMap] = useState<Map<number, IEventFeedResponse>>(() => {
        const map = new Map<number, IEventFeedResponse>();
        initialEvents?.forEach((e) => map.set(e.id, e));
        return map;
    });

    const gamesUpdateHandler = useCallback(
        (data: IEventFeedResponse[], error: IClientError) => {
            if (!error) {
                data.forEach((event) => {
                    if (statuses.indexOf(event.status) !== -1) {
                        gamesMap.delete(event.id);
                    } else {
                        gamesMap.set(event.id, event);
                    }
                });
                const games = Array.from(gamesMap.values());
                const sorted_games = games.sort(sortByScheduledTime);
                setGames((state) => ({
                    ...state,
                    loading: false,
                    data: sorted_games,
                }));
            } else {
                console.error(error);
            }
        },
        [gamesMap],
    );

    const payload = useMemo(() => {
        let result: ISubscriptionPayloadParams<"tournament">[0] = {
            tournament_id: tournamentId,
        };

        if (hoursRange) {
            result = { ...result, ...getTimefilterPayloadForSocket(hoursRange) };
        }

        return result;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tournamentId]); // do not add timeFilter here

    useWebsocketUpdates("tournament", gamesUpdateHandler, undefined, payload);

    useEffect(() => {
        return () => {
            if (tournamentId) {
                setGames((state) => ({
                    ...state,
                    loading: true,
                    data: [],
                }));
                gamesMap.clear();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tournamentId]);

    return { games: games.data, loading: games.loading };
};
